import {
  ColumnFilter,
  IDocumentService,
  ILookupService,
  LookupRecord,
  ObjectTyped,
} from '@price-for-profit/micro-services';
import {
  ABG_CUSTOMER_BRANDS,
  ABG_CUSTOMER_CHANNELS,
  ABG_CUSTOMER_TIERS,
  CLIENT_ID,
  CUSTOMERS_TABLE,
  CUSTOMER_TO_DELETE_DOCUMENT_TYPE,
} from 'shared/constants/app';
import {
  CustomerBrandsLookup,
  CustomerChannelsLookup,
  CustomerLookup,
  CustomerTiersLookup,
  CustomerToDelete,
} from 'shared/types/customer';
import {
  CustomerFilter,
  CustomerManagementGridItem,
  defaultCustomerFilters,
  CustomerNotificationTemplate,
  I2PEnvironment,
} from 'shared/types';
import { convertToKeyValueOf } from '@price-for-profit/micro-services';
import { getCustomerDeleteEmailTemplate } from 'shared/utility';
import { INotificationService } from './notification-service';

export interface SendCustomerNotificationProps {
  customer: CustomerLookup;
  template: CustomerNotificationTemplate;
  environment: I2PEnvironment;
  userName: string;
}

export interface ICustomerService {
  getCustomersWithFilters: (
    userBrands: string[] | null,
    filters?: CustomerFilter
  ) => Promise<CustomerManagementGridItem[]>;
  getCustomersLookupWithFilters: (filters?: CustomerFilter, userBrands?: string[] | null) => Promise<CustomerLookup[]>;
  getCustomerBrandsByCustomer: (customerName: string) => Promise<string[]>;
  createNewCustomer: (customer: CustomerLookup) => Promise<void>;
  deleteCustomerPermanent: (currentCustomer: CustomerLookup) => Promise<void>;
  getCustomerBrands: () => Promise<string[]>;
  getCustomerTiers: () => Promise<string[]>;
  getCustomerChannels: () => Promise<string[]>;
  createNewCustomerToDelete: (customer: CustomerToDelete) => Promise<void>;
  sendCustomerDeleteNotification: (props: SendCustomerNotificationProps) => Promise<void>;
}

export class CustomerService implements ICustomerService {
  private shared: { clientId: string } = { clientId: CLIENT_ID };
  constructor(
    private lookupService: ILookupService,
    private documentService: IDocumentService,
    private notificationService: INotificationService
  ) {}

  async getCustomersWithFilters(
    userBrands: string[] | null,
    filters: CustomerFilter = defaultCustomerFilters
  ): Promise<CustomerManagementGridItem[]> {
    try {
      const customers = await this.getCustomersLookupWithFilters(filters, userBrands);
      return customers.map(
        (customer): CustomerManagementGridItem => ({
          id: customer.uniqueid,
          masterCustomer: customer.abgmastercustomer,
          abgBrand: customer.brand,
          channel: customer.channel,
          lastTwelveMonthRevenue: parseFloat(customer.l12mproductgrossrevenue),
          customerTier: customer.customertier,
          numberOfSkus: parseInt(customer.numberofskus),
          grossToNetPercent: parseFloat(customer.totalgtn),
          numberOfContracts: parseInt(customer.numberofcontracts),
          pricingType: customer.pricingtype,
          priceList: customer.pricinglist,
          isDeleted: customer.isdeleted === 'TRUE',
          isMarkedForDeletion: customer.ismarkedfordeletion === 'TRUE',
        })
      );
    } catch (err) {
      return [];
    }
  }

  async getCustomersLookupWithFilters(
    filters: CustomerFilter = defaultCustomerFilters,
    userBrands?: string[] | null
  ): Promise<CustomerLookup[]> {
    const customerFilterMap: Record<keyof CustomerFilter, keyof CustomerLookup> = {
      abgBrand: 'brand',
      masterCustomer: 'abgmastercustomer',
      customerTier: 'customertier',
      channel: 'channel',
    };
    const columnFilters: ColumnFilter<CustomerLookup>[] = ObjectTyped.entries(filters).map(([key, value]) => ({
      columnName: customerFilterMap[key],
      dataType: 'string',
      queryOperator: 'contains',
      value,
    }));

    try {
      if (userBrands && userBrands.length > 0) {
        let customers: LookupRecord<CustomerLookup>[] = [];
        await Promise.all(
          userBrands.map(async userBrand => {
            if (columnFilters.some(f => f.columnName === 'brand')) {
              const brandIndex = columnFilters.findIndex(f => f.columnName === 'brand');
              columnFilters.splice(brandIndex, 1);
            }
            columnFilters.push({
              columnName: 'brand',
              dataType: 'string',
              queryOperator: 'eq',
              value: userBrand,
            });
            try {
              const brandCustomers = await this.lookupService.filteredAndSearch<CustomerLookup>({
                clientId: this.shared.clientId,
                table: CUSTOMERS_TABLE,
                columnFilters,
              });
              customers = [...customers, ...brandCustomers];
            } catch (err) {}
          })
        );
        return customers;
      } else {
        const customers = await this.lookupService.filteredAndSearch<CustomerLookup>({
          clientId: this.shared.clientId,
          table: CUSTOMERS_TABLE,
          columnFilters,
        });
        return customers;
      }
    } catch (err) {
      return [];
    }
  }

  async getCustomerBrandsByCustomer(customerName: string) {
    try {
      const customerBrands = await this.lookupService.filteredAndSearch<CustomerLookup>({
        clientId: this.shared.clientId,
        table: CUSTOMERS_TABLE,
        columnFilters: [
          { columnName: 'abgmastercustomer', dataType: 'string', queryOperator: 'eq', value: customerName },
        ],
      });

      return customerBrands.map((customer): string => customer.brand);
    } catch (err) {
      console.log({ err });
      return [];
    }
  }

  async getCustomerBrands() {
    try {
      const data = await this.lookupService.filteredAndSearch<CustomerBrandsLookup>({
        clientId: CLIENT_ID,
        table: ABG_CUSTOMER_BRANDS,
        columnFilters: [],
      });
      return data?.map(option => option.brand).sort();
    } catch (err) {
      throw err;
    }
  }

  async getCustomerTiers() {
    try {
      const data = await this.lookupService.filteredAndSearch<CustomerTiersLookup>({
        clientId: CLIENT_ID,
        table: ABG_CUSTOMER_TIERS,
        columnFilters: [],
      });
      return data?.map(option => option.tier).sort();
    } catch (err) {
      throw err;
    }
  }

  async getCustomerChannels() {
    try {
      const data = await this.lookupService.filteredAndSearch<CustomerChannelsLookup>({
        clientId: CLIENT_ID,
        table: ABG_CUSTOMER_CHANNELS,
        columnFilters: [],
      });
      return data?.map(option => option.channel).sort();
    } catch (err) {
      throw err;
    }
  }

  async createNewCustomer(customer: CustomerLookup) {
    try {
      const { version } = await this.lookupService.getActiveTableVersion({
        clientId: this.shared.clientId,
        table: CUSTOMERS_TABLE,
      });

      const lookupValues = convertToKeyValueOf([customer]);

      await this.lookupService.saveTableMembers<CustomerLookup>({
        clientId: this.shared.clientId,
        table: CUSTOMERS_TABLE,
        version,
        lookupValues,
      });
    } catch (err) {
      console.log({ err });
    }
  }

  async deleteCustomerPermanent(currentCustomer: CustomerLookup) {
    try {
      const { version } = await this.lookupService.getActiveTableVersion({
        clientId: this.shared.clientId,
        table: CUSTOMERS_TABLE,
      });

      //delete old member with old channel
      const members = convertToKeyValueOf([currentCustomer]);
      await this.lookupService.deleteTableMembers<CustomerLookup>({
        clientId: this.shared.clientId,
        table: CUSTOMERS_TABLE,
        version,
        members,
      });
    } catch (err) {
      console.log({ err });
    }
  }

  async createNewCustomerToDelete(customer: CustomerToDelete) {
    try {
      await this.documentService.create<CustomerToDelete, CustomerToDelete>({
        type: CUSTOMER_TO_DELETE_DOCUMENT_TYPE,
        clientId: CLIENT_ID,
        payload: customer,
      });
    } catch (err) {
      console.log({ err });
    }
  }

  async sendCustomerDeleteNotification({
    customer,
    template,
    environment,
    userName,
  }: SendCustomerNotificationProps): Promise<void> {
    await this.notificationService.sendEmail(
      getCustomerDeleteEmailTemplate({ customer, template, environment, userName })
    );
  }
}
